/**
 * Safe envelopes and the single classification authority for the experiments.
 *
 * `classify()` is the ONLY place the safe/warm/cold/damp/dry thresholds live.
 * Comfort Streams' bands, the Tapestry's cell colour, the Loop's "time inside
 * envelope" and the Postcards' "% safe" all resolve through it, which is what
 * keeps the eight screens telling the same story.
 */

/** The bands a reading can fall into. Exactly one per reading. */
export type ClimateBand = "safe" | "warm" | "cold" | "damp" | "dry";

export interface SafeEnvelope {
  /** Stable identifier, used as the `envelope` URL search param. */
  id: string;
  /** Short name, e.g. "Bizot". */
  name: string;
  /** Minimum safe temperature in °C (inclusive). */
  tMin: number;
  /** Maximum safe temperature in °C (inclusive). */
  tMax: number;
  /** Minimum safe relative humidity in % (inclusive). */
  rhMin: number;
  /** Maximum safe relative humidity in % (inclusive). */
  rhMax: number;
  /** One-line provenance, shown under the selector. */
  description: string;
}

/**
 * The Bizot green protocol — the default, and the one the designs label
 * `Bizot · 16–25 °C · 40–60 % RH`.
 */
export const BIZOT_ENVELOPE: SafeEnvelope = {
  id: "bizot",
  name: "Bizot",
  tMin: 16,
  tMax: 25,
  rhMin: 40,
  rhMax: 60,
  description: "Bizot green protocol — the interoperable loan standard",
};

export const SAFE_ENVELOPES: readonly SafeEnvelope[] = [
  BIZOT_ENVELOPE,
  {
    id: "ashrae-aa",
    // Numbers taken from `ASHRAE_AA_A1_A2` in `@charphq/shared`, which cites ASHRAE
    // Handbook—HVAC Applications Ch. 24 Table 13A's "Long-Term Outer Limits" column. This screen
    // previously carried 15–25 °C / 45–55 % RH, which is not a published ASHRAE bound — AA, A1 and
    // A2 share one set of outer limits and differ only in allowances that static bounds cannot
    // express. `__tests__/envelopes.test.ts` pins the two modules to the same figures.
    name: "ASHRAE AA",
    tMin: 10,
    tMax: 25,
    rhMin: 35,
    rhMax: 65,
    description: "ASHRAE AA / A1 / A2 long-term outer limits — a screening band, not a spec",
  },
  {
    id: "strict",
    name: "Strict",
    tMin: 18,
    tMax: 22,
    rhMin: 45,
    rhMax: 55,
    description: "Narrow band for mixed-media and panel paintings",
  },
] as const;

/** Look an envelope up by id, falling back to Bizot for unknown ids. */
export function envelopeById(id: string | undefined): SafeEnvelope {
  return SAFE_ENVELOPES.find((envelope) => envelope.id === id) ?? BIZOT_ENVELOPE;
}

/**
 * Human label for the selector.
 *
 * The designs drop the temperature half on humidity-only screens (Climate
 * Tapestry reads `Bizot · 40–60 % RH`), so the metric in view narrows the
 * label rather than there being a second component.
 */
export function envelopeLabel(envelope: SafeEnvelope, metric?: "temperature" | "humidity"): string {
  const temperature = `${envelope.tMin}–${envelope.tMax} °C`;
  const humidity = `${envelope.rhMin}–${envelope.rhMax} % RH`;
  if (metric === "temperature") return `${envelope.name} · ${temperature}`;
  if (metric === "humidity") return `${envelope.name} · ${humidity}`;
  return `${envelope.name} · ${temperature} · ${humidity}`;
}

/**
 * Classify a reading against an envelope.
 *
 * Bounds are **inclusive**: a reading sitting exactly on `tMin`/`tMax`/`rhMin`/
 * `rhMax` is safe. Only a reading strictly outside the band is an excursion.
 *
 * A reading can breach temperature and humidity at once (a hot damp August in
 * an unheated church), but every consumer needs exactly one band — the stream
 * widths, the cell colours and the "% safe" figures must sum to 100 %. So
 * precedence is fixed and **humidity wins**: relative humidity is the faster
 * agent of damage for the organic materials these collections are made of, so
 * a damp-and-warm site is reported as damp.
 *
 * Order: damp → dry → warm → cold → safe.
 */
export function classify(
  temperature: number,
  relativeHumidity: number,
  envelope: SafeEnvelope = BIZOT_ENVELOPE,
): ClimateBand {
  if (relativeHumidity > envelope.rhMax) return "damp";
  if (relativeHumidity < envelope.rhMin) return "dry";
  if (temperature > envelope.tMax) return "warm";
  if (temperature < envelope.tMin) return "cold";
  return "safe";
}

/** Band ordering used by legends and stacked charts (top of stack first). */
export const BAND_ORDER: readonly ClimateBand[] = ["damp", "warm", "safe", "dry", "cold"] as const;

/**
 * Legend copy per band. The thresholds interpolate from the envelope so the
 * legend never drifts from what `classify()` actually does.
 */
export function bandLabel(band: ClimateBand, envelope: SafeEnvelope = BIZOT_ENVELOPE): string {
  switch (band) {
    case "damp":
      return `> ${envelope.rhMax} % RH`;
    case "dry":
      return `< ${envelope.rhMin} % RH`;
    case "warm":
      return `> ${envelope.tMax} °C`;
    case "cold":
      return `< ${envelope.tMin} °C`;
    case "safe":
      return "inside envelope";
  }
}

/** Display name per band. */
export const BAND_NAME: Record<ClimateBand, string> = {
  safe: "Safe",
  warm: "Warm",
  cold: "Cold",
  damp: "Damp",
  dry: "Dry",
};
